import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http'; // angular 5+ method
import { Params } from '@angular/router';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs/Observable';

// import 'rxjs/add/operator/map';
// import 'rxjs/add/operator/concatMap';
// import 'rxjs/add/operator/catch';

import { map, concatMap, catchError, mergeMap } from "rxjs/operators";

import { SessionStorageService } from 'ngx-webstorage/dist/app';
import { Adal5Service } from './adal/adal5.service';

import { SecretService } from './secret.service';
import { AppStateService } from './AppStateService';
import { ClientSettingsService } from './ClientSettingsService';

import { CacheDataInfo, NameIndex, WidgetConfiguration, DataPolicy } from '../interfaces/WidgetInterfaces';
import { UserStatistics } from '../interfaces/UserInterfaces';
import { UtilityFunctions } from '../classes/UtilityFunctions';
import { DataSet, ManagedCacheHitLog, SideNavList } from '../interfaces/DisplayInterfaces';
import { ManagedDataReply, APIStoreTokenInfo, DataConsumerConfiguration } from '../interfaces/WidgetInterfaces';
import { collapseTextChangeRangesAcrossMultipleVersions } from 'typescript';


@Injectable()
export class WebPlatformApiService {

  apiStoreHeaderTokens: APIStoreTokenInfo[] = [];
  constructor(
    private appStateService: AppStateService,
    private httpClient: HttpClient,
    private adalService: Adal5Service,
    private secretService: SecretService,
    private sessionStorageService: SessionStorageService,
    private clientSettingsService: ClientSettingsService) {
  }

  GetBase<T>(urlSuffix: string, params?: Params, customHeaders?: string): Observable<T> {
    return this.getRequestOptions(this.clientSettingsService.WebApiUrl + urlSuffix, params, customHeaders).pipe(
      mergeMap(ro => {
        UtilityFunctions.Log(3, `Middle layer request to ${ro.url}`, this.clientSettingsService, this.appStateService);
        return this.httpClient.get(ro.url, { headers: ro.headers, params: ro.params }).pipe(
          catchError(this.handleError)
        )
      }),
      map(res => <T>res)
    )
  }

  GetMongoDocs(collectionName: string, jsonFilter?: any): Observable<Object[]> {
    if (!jsonFilter) { jsonFilter = {}; }
    return this.GetBase<Object[]>(`/api/MongoCollection?CollectionName=${collectionName}&jsonFilter=${JSON.stringify(jsonFilter)}`);
  }

  GetMongoDoc(collectionName: string, key: string, value: string): Observable<Object> {
    return this.GetBase<Object>(`/api/MongoCollection?CollectionName=${collectionName}&keyField=${key}&keyValue=${value}`);
  }

  UpsertMongoDoc(collectionName: string, key: string, document: any) {
    this.UpsertMongoDocObs(collectionName, key, document).subscribe();
  }

  UpsertMongoDocObs(collectionName: string, key: string, document: any): Observable<any> {

    const urlSuffix = `/api/MongoCollection?CollectionName=${collectionName}&keyField=${key}`;
    return this.getRequestOptions(this.clientSettingsService.WebApiUrl + urlSuffix)
      .pipe(
      mergeMap(ro => {
        UtilityFunctions.Log(3, `MongoCollection request to ${ro.url}`, this.clientSettingsService, this.appStateService);
        return this.httpClient.post(ro.url, document, { headers: ro.headers, params: ro.params });
      }),
      map(res => res)
      )
  }

  DeleteMongoDoc(collectionName: string, key: string, value: string): Observable<any> {

    const urlSuffix = `/api/MongoCollection?CollectionName=${collectionName}&keyField=${key}&keyValue=${value}`;
    return this.getRequestOptions(this.clientSettingsService.WebApiUrl + urlSuffix)
      .pipe(
      mergeMap(ro => {
        UtilityFunctions.Log(3, `MongoCollection request to ${ro.url}`, this.clientSettingsService, this.appStateService);
        /* I shoul check back for delete function */
        return this.httpClient.delete(ro.url, ro).map(data => data)
      })
      )
  }

  GetApiVersion(): Observable<string> {
    UtilityFunctions.Log(3, `GetApiVersion request to ${this.clientSettingsService.WebApiUrl}/ApiVersion`,
      this.clientSettingsService, this.appStateService);

    return this.httpClient
      .get(this.clientSettingsService.WebApiUrl + '/ApiVersion')
      .map(res => {
        const resp = <any>res;
        return resp.Version
      })
  }

  GetUserDesigns(): Observable<string[]> {
    return this.GetBase<string[]>('/EntitledDesignIds');
  }

  GetAllUserStats(): Observable<UserStatistics[]> {
    return this.GetBase<UserStatistics[]>('/AllUserStats');
  }

  GetUserStats(userName: string): Observable<UserStatistics> {
    return this.GetBase<UserStatistics>('/UserStats/' + userName);
  }

  UpsertUserStats(userStats: UserStatistics) {
    return this.httpClient.post(
      this.clientSettingsService.WebApiUrl + '/UserStats',
      userStats
    );
  }

  UpdateConfiguration(name: string, config: any): Observable<any> {
    const entry = { key: name, Payload: config };

    return this.getRequestOptions(this.clientSettingsService.WebApiUrl + '/Configuration').pipe(
      mergeMap(ro => {
        UtilityFunctions.Log(3, `MongoCollection request to ${ro.url}`, this.clientSettingsService, this.appStateService);
        return this.httpClient.post(ro.url, entry, { headers: ro.headers, params: ro.params });
      })
    )
  }

  SaveSingleConfiguration(name: string, config: SideNavList | WidgetConfiguration | DataPolicy, configName: string): Observable<any> {
    return this.getRequestOptions(this.clientSettingsService.WebApiUrl + `/Configuration/${name}`).pipe(
      mergeMap(ro => {
        UtilityFunctions.Log(3, `MongoCollection request to ${ro.url}`, this.clientSettingsService, this.appStateService);
        return this.httpClient.post(ro.url, config, { headers: ro.headers, params: ro.params });
      })
    )
  }

  RemoveSingleConfig(name: string, configName: string): Observable<any> {
    return this.getRequestOptions(this.clientSettingsService.WebApiUrl + `/Configuration/${name}?config=${configName}`).pipe(
      mergeMap(ro => {
        UtilityFunctions.Log(3, `MongoCollection request to ${ro.url}`, this.clientSettingsService, this.appStateService);
        return this.httpClient.delete(ro.url, { headers: ro.headers, params: ro.params })
      })
    )
  }

  GetServerConfigurationSettings(): Observable<any[]> {
    return this.GetBase<any[]>('/api/ConfigureView/AllInfo');
  }

  GetConfig<T>(name: string): Observable<T> {
    return this.GetBase<T>('/Configuration/' + name);
  }

  GetManagedCacheHitLogs(): Observable<ManagedCacheHitLog[]> {
    return this.GetBase<ManagedCacheHitLog[]>('/ManagedCacheHitLogs');
  }

  UpdateCacheStatus(minutesAhead: number): Observable<string[][]> {
    return this.GetBase<string[][]>('/UpdateCacheData/' + minutesAhead.toString());
  }

  GetManagedCacheDataStatus(): Observable<string[][]> {
    return this.GetBase<string[][]>('/ManagedCacheDataStatus');
  }

  GetIsAdmin(): Observable<boolean> {
    return this.GetBase<NameIndex>('/UserIsAdmin')
      .map(res => { return res.index === 1 ? true : false; });
  }

  GetCacheDataByKey(key: string): Observable<CacheDataInfo> {
    return this.GetBase<CacheDataInfo>(`/CurrentCacheByKey?key=${key}`);
  }

  ForceUpdateByKey(key: string): Observable<string[][]> {
    return this.GetBase<string[][]>(`/UpdateCacheDataItemKey?key=${key}`);
  }

  private getRequestOptions(url: string, params?: Params, customHeaders?: string): Observable<{ url, headers, params?}> {

    if (params) {
      let x: HttpParams = new HttpParams();
      Object.keys(params).forEach(k => x = x.append(k, params[k]));

      return this.getAuthHeadersOrLoginAsync()
        .map<HttpHeaders, { url, headers, params }>(h => {
          let requestOptions = {
            url: url,
            headers: h,
            params: x
          };
          if (customHeaders) {
            requestOptions.headers = requestOptions.headers.append('customHeaders', customHeaders)
          }
          return requestOptions;
        });
    } else {
      return this.getAuthHeadersOrLoginAsync()
        .map<HttpHeaders, { url, headers, params?}>(h => {
          let requestOptions = {
            url: url,
            headers: h
          };
          if (customHeaders) {
            requestOptions.headers = requestOptions.headers.append('customHeaders', customHeaders)
          }
          return requestOptions;
        });
    }
  }

  GetManagedDataReply(name: string, params: Params, customHeaders?: string): Observable<ManagedDataReply> {
    if (customHeaders) {
      return this.GetBase<ManagedDataReply>('/ManagedData/' + name, params, customHeaders);
    } else {
      return this.GetBase<ManagedDataReply>('/ManagedData/' + name, params);
    }
  }

  public checkAndRefreshAuthToken(x: (updated: boolean) => void) {
    this.adalService.refreshDataFromCache();

    if (!this.adalService.userInfo.authenticated) {
      this.adalService.acquireToken(this.secretService.adalConfig.clientId).subscribe( r => { x(true); })
    }
    else 
    {
      x(false);      
    }    
  }

  private getAuthHeadersOrLoginAsync(): Observable<HttpHeaders> {
    this.adalService.refreshDataFromCache();

    if (this.clientSettingsService.SkipAuthentication) {
      return Observable.of<HttpHeaders>(
        new HttpHeaders({
          'Accept': 'application/json'
        })
      );
    } else if (!this.adalService.userInfo.authenticated) {
      this.appStateService.appendLog('user is not authenticated or auth is stale: redirecting to login', 2);
      return this.adalService.acquireToken(this.secretService.adalConfig.clientId).map<string, HttpHeaders>(response => {
        return new HttpHeaders({
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + this.adalService.getCachedToken(this.secretService.adalConfig.clientId)
        });
      });

    } else {
      return Observable.of<HttpHeaders>(
        new HttpHeaders({
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + this.adalService.getCachedToken(this.secretService.adalConfig.clientId)
        }));
    }

  }

  private handleError(error: HttpErrorResponse | any) {
    let errMsg: string;
    if (error instanceof HttpErrorResponse) {
      const body = error || '';
      const err = error.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return Observable.throw(errMsg);
  }

  GetGenericDataReply(dataUri: string, dataConsumerConfig: DataConsumerConfiguration , body?: any, mtAdfsOpenId?: string): Observable<any> {
    const apiStoreAppKey = dataConsumerConfig.apiStoreAppKey;
    const httpMethod = dataConsumerConfig.httpMethod;
    const sendMtAdfsOpenId = dataConsumerConfig.apiStoreSendMtAdfsOpenId;
    UtilityFunctions.Log(3, `Generic API request to ${dataUri}`, this.clientSettingsService, this.appStateService);
    const method = (httpMethod === null || httpMethod === undefined) ? 'get' : httpMethod;
    const hasBody = (body !== null && body !== undefined && body !== '');
    if (apiStoreAppKey === null || apiStoreAppKey === undefined) {
      let headers: HttpHeaders = new HttpHeaders();
      if (dataConsumerConfig.overrideCustomHeaders.length) {
        dataConsumerConfig.overrideCustomHeaders.forEach(och => {
          headers = headers.append(och.name, och.value);
        })
      }
      if (hasBody) {
        return this.httpClient[method](dataUri, body, { headers }).pipe(
          catchError(this.handleError)
        )

      } else {
        return this.httpClient[method](dataUri, { headers }).pipe(
          catchError(this.handleError)
        )
      }
    } else {
      // baseUrl is the first part of the url (right after http:// and before any other slashes)
      const baseUrl = dataUri.split('/').splice(0, 3).join('/') + '/';
      return this.getAPIToken(baseUrl, apiStoreAppKey).pipe(
        concatMap((token) => {
          let headers: HttpHeaders;
          if (sendMtAdfsOpenId && mtAdfsOpenId) {
            headers = new HttpHeaders()
              .set('authorization', token.toString())
              .set('x-mt-adfs-openid', mtAdfsOpenId);
          } else {
            headers = new HttpHeaders()
              .set('authorization', token.toString());
          }
          if (dataConsumerConfig.overrideCustomHeaders.length) {
            dataConsumerConfig.overrideCustomHeaders.forEach(och => {
              headers = headers.append(och.name, och.value);
            })
          }
          if (hasBody) {
            return this.httpClient[method](dataUri, body, { headers }).pipe(
              catchError(this.handleError)
            )
          } else {
            return this.httpClient[method](dataUri, { headers }).pipe(
              catchError(this.handleError)
            )
          }
        })
      )
    }
  }

  getAPIToken(apistoreBaseUrl: string, apistoreAppKey: string): Observable<string> {
    const ckToken = this.apiStoreHeaderTokens.find(tk => tk.appKey === apistoreAppKey);
    if (!ckToken || new Date() > ckToken.expiration) {
      const url = apistoreBaseUrl + 'token';
      const body = 'grant_type=client_credentials';
      const headers = new HttpHeaders()
        .set('authorization', apistoreAppKey)
        .set('content-type', 'application/x-www-form-urlencoded');
      return this.httpClient.post(url, body, { headers: headers }).pipe(
        map((tokenResp: any) => {
          const apiHeaderToken = tokenResp.token_type + ' ' + tokenResp.access_token;
          const apiHeaderTokenExpiration = new Date(new Date().getTime() + tokenResp.expires_in * 1000);
          if (ckToken && new Date() > ckToken.expiration) { // if expired first remove the expired one
            const ix = this.apiStoreHeaderTokens.map(tk => tk.appKey).indexOf(apistoreAppKey);
            this.apiStoreHeaderTokens.splice(ix, 1);
          }
          this.apiStoreHeaderTokens.push({
            token: apiHeaderToken,
            expiration: apiHeaderTokenExpiration,
            appKey: apistoreAppKey
          });
          return apiHeaderToken;
        })
      )

    } else {
      return Observable.of(ckToken.token);
    }
  }
}
